import React, { FC, HTMLAttributes } from 'react';
import { tint } from 'src/shared/globals';
import BiCircle from 'src/shared/material_icons/toggle/radio_button_unchecked.svg';
import styled from 'styled-components';

// Re-export ui-widgets badge components
export * from './assignmentBadge';

export type BadgeVariant = 'success' | 'warning' | 'danger' | 'neutral' | 'info' | 'store' | 'standalone' | 'pod' | 'danger-solid';

const badgeVariantStyles: Record<BadgeVariant, { background: string; color: string }> = {
    success: { background: '#d6f3e2', color: '#065f46' },
    warning: { background: '#fef3c7', color: '#92400e' },
    danger: { background: '#fee2e2', color: '#991b1b' },
    neutral: { background: '#f3f4f6', color: '#374151' },
    info: { background: '#dbeafe', color: '#1e40af' },
    store: { background: '#dbeafe', color: '#1e40af' },
    standalone: { background: '#fae8ff', color: '#86198f' },
    pod: { background: '#dcfce7', color: '#166534' },
    'danger-solid': { background: '#ec7063', color: '#ffffff' }
};

export interface BadgeProps extends HTMLAttributes<HTMLDivElement> {
    variant?: BadgeVariant;
    badgeColor?: string;
    borderColor?: string;
    color?: string;
    showCircle?: boolean;
    children?: React.ReactNode;
}

const StyledBadge = styled.div<{ $badgeColor: string; $borderColor?: string; $color?: string }>`
    background-color: ${(props) => props.$badgeColor};
    display: inline-flex;
    align-items: center;
    width: fit-content;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    line-height: 1.4;
    white-space: nowrap;
    color: ${(props) => props.$color || 'inherit'};
`;

const Circle = styled(BiCircle)`
    margin-right: 2px;
`;

export const Badge: FC<BadgeProps> = ({ variant, badgeColor, color, showCircle, children, className, ...rest }) => {
    const variantStyle = variant ? badgeVariantStyles[variant] : undefined;
    const resolvedBadgeColor = badgeColor ?? variantStyle?.background ?? '#f3f4f6';
    const resolvedColor = color ?? variantStyle?.color;

    return (
        <StyledBadge {...rest} className={className} $badgeColor={resolvedBadgeColor} $color={resolvedColor}>
            {showCircle && <Circle color={tint(resolvedBadgeColor, -16)} />}
            {children}
        </StyledBadge>
    );
};
